import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Box, CachedBox } from '../../model/types';
import { getBoxRateField } from '../helpers/getBoxRateField';
import { WarehouseForBooking } from 'entities/Warehouse';
import { getBoxPriceAmountWithVat } from '../helpers/getBoxPriceAmountWithVat';
import { UserAccountType } from 'entities/User';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

export const useGetBoxRentalPriceStringWithVat = (
  boxInfo: Nullable<Box> | Nullable<CachedBox>,
  invoiceFrequencyType?: InvoiceFrequencyType,
  warehouseInfo?: Nullable<WarehouseForBooking>,
  accountType?: Nullable<UserAccountType>,
): string | undefined => {
  const { t } = useAppTranslation('booking');

  const currencySymbol = useGetCurrencySymbol();

  if (!boxInfo) {
    return undefined;
  }

  if (invoiceFrequencyType) {
    const rateField = getBoxRateField(invoiceFrequencyType);

    if (rateField && boxInfo[rateField]) {
      const rentalPrice = roundNumber((boxInfo[rateField] as number) * boxInfo.priceFactor);
      const boxPriceWithVat = getBoxPriceAmountWithVat(rentalPrice, warehouseInfo, accountType);

      return `${formatPrice(boxPriceWithVat)} ${currencySymbol}/${t(invoiceFrequencyType)}`;
    }

    return undefined;
  }

  if (boxInfo?.monthRate) {
    const rentalPrice = roundNumber(boxInfo.monthRate * boxInfo.priceFactor);
    const boxPriceWithVat = getBoxPriceAmountWithVat(rentalPrice, warehouseInfo, accountType);

    return `${formatPrice(boxPriceWithVat)} ${currencySymbol}/${t('month')}`;
  }

  if (boxInfo?.weekRate) {
    const rentalPrice = roundNumber(boxInfo.weekRate * boxInfo.priceFactor);
    const boxPriceWithVat = getBoxPriceAmountWithVat(rentalPrice, warehouseInfo, accountType);

    return `${formatPrice(boxPriceWithVat)} ${currencySymbol}/${t('week')}`;
  }

  if (boxInfo?.dailyRate) {
    const rentalPrice = roundNumber(boxInfo.dailyRate * boxInfo.priceFactor);
    const boxPriceWithVat = getBoxPriceAmountWithVat(rentalPrice, warehouseInfo, accountType);

    return `${formatPrice(boxPriceWithVat)} ${currencySymbol}/${t('day')}`;
  }
};
